import { DIALOG_DATA } from '@angular/cdk/dialog';
import { computed, DestroyRef, inject, InjectionToken, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '@core/authentication/auth.service';
import {
  EmailAttachment,
  EmailPngPreviewDialogData,
} from '@v2/modules/email-app/email-app.interfaces';
import { Attachment } from '@v2/modules/email-app/services/email-oauth.service';
import { EmailOcrPngFile, EmailOcrService } from '@v2/modules/email-app/services/email-ocr.service';
import { map, Observable, of, shareReplay, switchMap } from 'rxjs';

const PREVIEW_ERROR = 'Preview could not be loaded.';

/**
 * State of the full-view preview: which PDF of the message is shown, its PNG pages
 * (converted on demand and cached), and per-attachment loading / error state.
 */
export class EmailPngPreviewDialogStore {
  private data = inject<EmailPngPreviewDialogData>(DIALOG_DATA);
  private emailOcrService = inject(EmailOcrService);
  private authService = inject(AuthService);
  private destroyRef = inject(DestroyRef);

  // The PDFs of the message the panel preview was opened from. An image opened from
  // the panel isn't one of them, so it is shown on its own, without navigation.
  readonly pdfs: EmailAttachment[] = (this.data.source?.attachments ?? []).filter(a =>
    (a.filename || a.name).toLowerCase().endsWith('.pdf')
  );

  readonly currentIndex = signal(this.pdfs.findIndex(a => a.id === this.data.source?.currentId));
  readonly current = computed(() => this.pdfs[this.currentIndex()] ?? null);
  readonly canNavigate = computed(() => this.currentIndex() >= 0 && this.pdfs.length > 1);
  readonly hasPrevious = computed(() => this.canNavigate() && this.currentIndex() > 0);
  readonly hasNext = computed(
    () => this.canNavigate() && this.currentIndex() < this.pdfs.length - 1
  );

  // PNG pages per attachment id, so stepping back to a PDF doesn't convert it again.
  // Seeded with what the panel already shows.
  private pngFilesById = signal<Record<string, EmailOcrPngFile[]>>(
    this.data.source ? { [this.data.source.currentId]: this.data.pngFiles } : {}
  );
  private loadingIds = signal<Set<string>>(new Set());
  private errorsById = signal<Record<string, string>>({});

  readonly pngFiles = computed(() => {
    const current = this.current();
    return current ? (this.pngFilesById()[current.id] ?? []) : this.data.pngFiles;
  });
  readonly isLoading = computed(() => {
    const current = this.current();
    return !!current && this.loadingIds().has(current.id);
  });
  readonly error = computed(() => {
    const current = this.current();
    return current ? (this.errorsById()[current.id] ?? null) : null;
  });

  // Fetched at most once, and only if a PDF without base64 data is reached.
  private messageAttachments$?: Observable<Attachment[]>;

  previous(): void {
    if (this.hasPrevious()) this.show(this.currentIndex() - 1);
  }

  next(): void {
    if (this.hasNext()) this.show(this.currentIndex() + 1);
  }

  retry(): void {
    this.show(this.currentIndex());
  }

  private show(index: number): void {
    this.currentIndex.set(index);

    const attachment = this.pdfs[index];
    if (this.pngFilesById()[attachment.id] || this.loadingIds().has(attachment.id)) return;

    this.setLoading(attachment.id, true);
    this.errorsById.update(errors => {
      const { [attachment.id]: _, ...rest } = errors;
      return rest;
    });

    // Each PDF is converted on its own, so stepping past one that is still loading
    // doesn't cancel it — it lands in the cache for when the user comes back.
    this.attachmentData(attachment)
      .pipe(
        switchMap(data => {
          if (!data) throw new Error('Attachment data is not available.');

          return this.emailOcrService.uploadPdfForOcr(
            {
              Filename: attachment.filename || attachment.name,
              ContentType: attachment.contentType || 'application/pdf',
              Data: data,
              Size: attachment.size || 0,
            },
            this.authService.getUser().tenantId
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: result => {
          this.pngFilesById.update(files => ({
            ...files,
            [attachment.id]: result.generatedPngs,
          }));
          this.setLoading(attachment.id, false);
        },
        error: (error: unknown) => {
          console.error('Email PDF preview failed:', error);
          // Don't replay a failed attachments fetch on retry
          this.messageAttachments$ = undefined;
          this.errorsById.update(errors => ({ ...errors, [attachment.id]: PREVIEW_ERROR }));
          this.setLoading(attachment.id, false);
        },
      });
  }

  private attachmentData(attachment: EmailAttachment): Observable<string | undefined> {
    if (attachment.data) return of(attachment.data);

    const source = this.data.source;
    if (!source) return of(undefined);

    this.messageAttachments$ ??= source.loadAttachments().pipe(shareReplay(1));
    const filename = attachment.filename || attachment.name;
    return this.messageAttachments$.pipe(
      map(attachments => attachments.find(a => a.Filename === filename)?.Data)
    );
  }

  private setLoading(id: string, loading: boolean): void {
    this.loadingIds.update(ids => {
      const next = new Set(ids);
      if (loading) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }
}

export const EMAIL_PNG_PREVIEW_DIALOG_STORE = new InjectionToken<EmailPngPreviewDialogStore>(
  'EMAIL_PNG_PREVIEW_DIALOG_STORE'
);

export const EMAIL_PNG_PREVIEW_DIALOG_STORE_PROVIDER = {
  provide: EMAIL_PNG_PREVIEW_DIALOG_STORE,
  useFactory: () => new EmailPngPreviewDialogStore(),
};
